import { useQuery } from "@tanstack/react-query"

import { api } from "@/lib/api"

/**
 * Dashboard KPIs — single round-trip for the four cards on the landing
 * page. Mirrors the backend's GET /admin/dashboard/stats response.
 *
 * Re-fetched on window focus + on a 60s interval so admins watching the
 * page see fresh numbers when content / signups land. Numbers are
 * cheap counts on indexed fields, so polling is fine.
 */

export type DashboardStats = {
  users: number
  videos: number
  activeSubscriptions: number
  pendingReports: number
}

type RawEnvelope = {
  success: boolean
  data: DashboardStats
  message?: string
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: async () => {
      const { data } = await api.get<RawEnvelope>("/admin/dashboard/stats")
      // Backend wraps in the legacy { success, data, message } envelope.
      // Unwrap so the component reads a clean DashboardStats object.
      return data.data
    },
    // 60s refetch interval; refetch on window focus too so the cards
    // catch up immediately when the admin tabs back in.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // Keep showing the previous numbers while a refetch is in flight,
    // so cards don't flash to em-dashes between polls.
    staleTime: 30_000,
  })
}
